import type { Locale } from './routes.mjs';

/**
 * Dates in the app must follow the language the page is rendered in, not the
 * language the browser happens to be set to. `toLocaleDateString(undefined)`
 * reads navigator.language, so a German page opened in a Russian Chrome
 * printed "20 июл. 2026 г." next to German labels. Mapping the app locale to a
 * fixed BCP 47 tag keeps every surface consistent for every visitor.
 */
const LOCALE_TAGS: Record<Locale, string> = { en: 'en-US', de: 'de-DE', es: 'es-ES' };

export const localeTag = (locale: Locale): string => LOCALE_TAGS[locale] ?? LOCALE_TAGS.en;

const DEFAULT_OPTIONS: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

/** Formats an ISO date in the page's locale. Returns `fallback` for missing or unparsable input. */
export function formatDate(
  value: string | number | Date | null | undefined,
  locale: Locale,
  options: Intl.DateTimeFormatOptions = DEFAULT_OPTIONS,
  fallback = '-',
): string {
  if (value === null || value === undefined || value === '') return fallback;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? fallback : date.toLocaleDateString(localeTag(locale), options);
}

/** Formats an ISO timestamp (date + time) in the page's locale. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  locale: Locale,
  options?: Intl.DateTimeFormatOptions,
  fallback = '-',
): string {
  if (value === null || value === undefined || value === '') return fallback;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? fallback : date.toLocaleString(localeTag(locale), options);
}
